'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import MfaStep, { type MfaChallenge } from './MfaStep';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface DemoCredential {
  role: string;
  email: string;
  password: string;
  color: string;
}

const demoCredentials: DemoCredential[] = [];

export default function LoginForm() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    setError,
  } = useForm<LoginFormData>({ defaultValues: { rememberMe: false } });

  const [mfa, setMfa] = useState<MfaChallenge | null>(null);

  const goHome = async (role: string) => {
    toast.success('Welcome back!', { duration: 2000 });
    await new Promise((r) => setTimeout(r, 400));
    window.location.href = role === 'member' ? '/member-portal' : '/dashboard';
  };

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: data.email, password: data.password }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError('email', { message: '' });
        setError('password', { message: body.error ?? 'Invalid email or password' });
        return;
      }
      if (body.mfaRequired || body.mfaSetupRequired) {
        setMfa({
          ticket: body.ticket,
          setup: body.mfaSetupRequired
            ? { secret: body.secret, otpauthUrl: body.otpauthUrl, passkey: body.passkeySetup }
            : undefined,
          passkey: body.passkey ?? null,
          totp: !!body.totp,
        });
        return;
      }
      await goHome(body.user?.user_metadata?.role ?? '');
    } catch {
      setError('password', { message: 'Could not reach the server. Try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const autofill = (cred: DemoCredential) => {
    setValue('email', cred.email, { shouldValidate: true });
    setValue('password', cred.password, { shouldValidate: true });
  };

  if (mfa)
    return (
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-[440px]">
          <MfaStep challenge={mfa} onDone={goHome} onCancel={() => setMfa(null)} />
        </div>
      </div>
    );

  return (
    <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
      <div className="w-full max-w-[440px]">
        {/* Mobile logo */}
        <div className="flex lg:hidden items-center gap-2 mb-8">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: 'var(--brand)' }}
          >
            <Icon name="BuildingLibraryIcon" size={18} className="text-white" />
          </div>
          <span className="font-bold text-xl" style={{ color: 'var(--ink)' }}>
            WENLSU Alumni SACCO
          </span>
        </div>

        <h2 className="text-2xl font-bold mb-1" style={{ color: 'var(--ink)' }}>
          Sign in to your account
        </h2>
        <p className="text-sm mb-8" style={{ color: 'hsl(40,6%,50%)' }}>
          Enter your credentials to access the management platform
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
          {/* Email */}
          <div>
            <label
              className="block text-sm font-600 mb-1.5"
              style={{ color: 'var(--brand)', fontWeight: 600 }}
            >
              Email address
            </label>
            <input
              type="email"
              autoComplete="email"
              placeholder="your.name@wenslu-sacco.org"
              className={`input-field ${errors.email ? 'input-error' : ''}`}
              {...register('email', {
                required: 'Email address is required',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Enter a valid email address',
                },
              })}
            />
            {errors.email && errors.email.message && (
              <p className="text-xs mt-1.5 flex items-center gap-1" style={{ color: '#C0392B' }}>
                <Icon name="ExclamationCircleIcon" size={12} />
                {errors.email.message}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label
                className="block text-sm font-600"
                style={{ color: 'var(--brand)', fontWeight: 600 }}
              >
                Password
              </label>
              <button
                type="button"
                className="text-xs font-500 transition-colors"
                style={{ color: 'var(--brand-link)' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--brand)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--brand-link)')}
                onClick={() => router.push('/auth/password-reset')}
              >
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Enter your password"
                className={`input-field pr-10 ${errors.password ? 'input-error' : ''}`}
                {...register('password', { required: 'Password is required' })}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                style={{ color: 'hsl(40,6%,58%)' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--brand)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'hsl(40,6%,58%)')}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
              </button>
            </div>
            {errors.password && (
              <p className="text-xs mt-1.5 flex items-start gap-1" style={{ color: '#C0392B' }}>
                <Icon name="ExclamationCircleIcon" size={12} className="mt-0.5 flex-shrink-0" />
                {errors.password.message}
              </p>
            )}
          </div>

          {/* Remember me */}
          <div className="flex items-center gap-2.5">
            <input
              type="checkbox"
              id="rememberMe"
              className="w-4 h-4 rounded accent-primary"
              style={{ accentColor: 'var(--brand)' }}
              {...register('rememberMe')}
            />
            <label
              htmlFor="rememberMe"
              className="text-sm cursor-pointer"
              style={{ color: 'hsl(40,6%,40%)' }}
            >
              Keep me signed in for 30 days
            </label>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full flex items-center justify-center gap-2"
            style={{ padding: '12px 20px', fontSize: 15 }}
          >
            {isLoading ? (
              <>
                <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                <span>Signing in…</span>
              </>
            ) : (
              <>
                <Icon name="LockClosedIcon" size={16} />
                <span>Sign in to WENLSU SACCO</span>
              </>
            )}
          </button>
        </form>

        {/* Demo credentials */}
        <div className="mt-8">
          <button
            type="button"
            onClick={() => setShowCredentials(!showCredentials)}
            className="flex items-center gap-2 text-sm font-600 mb-3 w-full"
            style={{ color: 'var(--brand-link)', fontWeight: 600 }}
          >
            <Icon name="UserCircleIcon" size={16} />
            Administrator-managed accounts
            <Icon
              name={showCredentials ? 'ChevronUpIcon' : 'ChevronDownIcon'}
              size={14}
              className="ml-auto"
            />
          </button>

          {showCredentials && (
            <div
              className="rounded-xl overflow-hidden fade-in"
              style={{ border: '1px solid hsl(40,6%,88%)' }}
            >
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ background: 'hsl(40,6%,96%)' }}>
                    <th
                      className="text-left px-3 py-2 font-600"
                      style={{ color: 'hsl(40,6%,40%)', fontWeight: 600 }}
                    >
                      Role
                    </th>
                    <th
                      className="text-left px-3 py-2 font-600"
                      style={{ color: 'hsl(40,6%,40%)', fontWeight: 600 }}
                    >
                      Email
                    </th>
                    <th className="px-3 py-2 w-14"></th>
                  </tr>
                </thead>
                <tbody>
                  {demoCredentials.map((cred) => (
                    <tr
                      key={`cred-${cred.role}`}
                      className="border-t cursor-pointer transition-colors"
                      style={{ borderColor: 'hsl(40,6%,92%)' }}
                      onClick={() => autofill(cred)}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'hsl(40,6%,97%)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <td className="px-3 py-2.5">
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-white font-600"
                          style={{ background: cred.color, fontSize: 10, fontWeight: 600 }}
                        >
                          {cred.role}
                        </span>
                      </td>
                      <td
                        className="px-3 py-2.5 font-mono-data"
                        style={{
                          color: 'hsl(40,6%,35%)',
                          fontFamily: 'IBM Plex Mono, monospace',
                          fontSize: 10,
                        }}
                      >
                        {cred.email}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-500 transition-colors"
                          style={{
                            background: 'var(--brand-soft)',
                            color: 'var(--brand)',
                            fontWeight: 500,
                          }}
                        >
                          Use
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-xs text-center mt-6" style={{ color: 'hsl(40,6%,60%)' }}>
          By signing in, you agree to WENLSU Alumni SACCO&apos;s{' '}
          <span className="underline cursor-pointer" style={{ color: 'var(--brand-link)' }}>
            Terms of Service
          </span>{' '}
          and{' '}
          <span className="underline cursor-pointer" style={{ color: 'var(--brand-link)' }}>
            Privacy Policy
          </span>
        </p>
      </div>
    </div>
  );
}
